import React, { useState, useEffect } from 'react';
import { Table, Card, Tag, Input, Space, Select, Modal, Descriptions, message, Popconfirm, Button } from 'antd';
import { DeleteOutlined, EyeOutlined } from '@ant-design/icons';
import apiClient from '../services/api';

const DEPARTMENTS = [
  'General Inquiry',
  'Order Support',
  'Delivery Query',
  'Product Advice',
  'Complaint',
  'Other',
];

const ContactSubmissions = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 });
  const [search, setSearch] = useState('');
  const [department, setDepartment] = useState(undefined);
  const [detail, setDetail] = useState(null);

  const fetchData = async (page = 1, pageSize = 20) => {
    setLoading(true);
    try {
      const params = { page, per_page: pageSize };
      if (search) params.search = search;
      if (department) params.department = department;
      const res = await apiClient.get('/admin/contact-submissions', { params });
      const d = res.data;
      setData(d.data || []);
      setPagination((prev) => ({
        ...prev,
        current: d.current_page || page,
        total: d.total || 0,
      }));
    } catch {
      message.error('Failed to load submissions');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, department]);

  const handleDelete = async (id) => {
    try {
      await apiClient.delete(`/admin/contact-submissions/${id}`);
      message.success('Submission deleted');
      fetchData(pagination.current, pagination.pageSize);
    } catch {
      message.error('Failed to delete');
    }
  };

  const columns = [
    { title: 'ID', dataIndex: 'id', key: 'id', width: 60 },
    { title: 'Name', dataIndex: 'name', key: 'name' },
    { title: 'Email', dataIndex: 'email', key: 'email' },
    { title: 'Phone', dataIndex: 'phone', key: 'phone', render: (v) => v || '-' },
    { title: 'Subject', dataIndex: 'subject', key: 'subject' },
    {
      title: 'Department',
      dataIndex: 'department',
      key: 'department',
      render: (v) => v ? <Tag>{v}</Tag> : '-',
    },
    {
      title: 'Message',
      dataIndex: 'message',
      key: 'message',
      ellipsis: true,
      width: 250,
    },
    { title: 'Date', dataIndex: 'created_at', key: 'created_at', width: 180 },
    {
      title: 'Actions',
      key: 'actions',
      width: 100,
      render: (_, record) => (
        <Space>
          <Button size="small" icon={<EyeOutlined />} onClick={() => setDetail(record)} />
          <Popconfirm title="Delete this submission?" onConfirm={() => handleDelete(record.id)}>
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Card title="Contact Submissions">
      <Space style={{ marginBottom: 16 }} wrap>
        <Input.Search
          placeholder="Search name, email, subject..."
          onSearch={(val) => { setSearch(val); fetchData(1, pagination.pageSize); }}
          allowClear
          style={{ width: 300 }}
        />
        <Select
          placeholder="Filter by department"
          allowClear
          style={{ width: 200 }}
          value={department}
          onChange={(val) => { setDepartment(val); fetchData(1, pagination.pageSize); }}
          options={DEPARTMENTS.map((d) => ({ label: d, value: d }))}
        />
      </Space>
      <Table
        columns={columns}
        dataSource={data}
        rowKey="id"
        loading={loading}
        pagination={{
          ...pagination,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (t) => `Total ${t} submissions`,
        }}
        onChange={(p) => fetchData(p.current, p.pageSize)}
      />
      <Modal
        title="Submission Details"
        open={!!detail}
        onCancel={() => setDetail(null)}
        footer={null}
        width={600}
      >
        {detail && (
          <Descriptions column={1} bordered size="small">
            <Descriptions.Item label="Name">{detail.name}</Descriptions.Item>
            <Descriptions.Item label="Email">{detail.email}</Descriptions.Item>
            <Descriptions.Item label="Phone">{detail.phone || '-'}</Descriptions.Item>
            <Descriptions.Item label="Subject">{detail.subject}</Descriptions.Item>
            <Descriptions.Item label="Department">{detail.department || '-'}</Descriptions.Item>
            <Descriptions.Item label="Message">{detail.message}</Descriptions.Item>
            <Descriptions.Item label="Submitted">{detail.created_at}</Descriptions.Item>
          </Descriptions>
        )}
      </Modal>
    </Card>
  );
};

export default ContactSubmissions;
